import React, { useEffect, useRef } from 'react'
import './Header.scss'
import { motion } from 'framer-motion'
import Typewriter from 'typewriter-effect'
import { images } from '../../constants'
import { AppWrap } from '../../wrapper'
import { SocialMedia } from '../../components'

// Neural Network Background Component
const NeuralBackground = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let nodes = [];
    
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    resize();
    window.addEventListener('resize', resize);
    
    // Create nodes
    const createNodes = () => {
      nodes = [];
      const nodeCount = Math.floor((canvas.width * canvas.height) / 15000);
      for (let i = 0; i < nodeCount; i++) {
        nodes.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
          radius: Math.random() * 2 + 1,
          pulsePhase: Math.random() * Math.PI * 2
        });
      }
    };
    
    createNodes();
    
    const draw = (time) => {
      ctx.fillStyle = 'rgba(250, 250, 250, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Update and draw nodes
      nodes.forEach((node, i) => {
        node.x += node.vx;
        node.y += node.vy;
        node.pulsePhase += 0.02;
        
        // Bounce off edges
        if (node.x < 0 || node.x > canvas.width) node.vx *= -1;
        if (node.y < 0 || node.y > canvas.height) node.vy *= -1;
        
        // Draw connections
        nodes.slice(i + 1).forEach(other => {
          const dx = other.x - node.x;
          const dy = other.y - node.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < 150) {
            const opacity = (1 - distance / 150) * 0.3;
            ctx.strokeStyle = `rgba(16, 185, 129, ${opacity})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(other.x, other.y);
            ctx.stroke();
          }
        });
        
        // Draw node with pulse effect
        const pulseSize = Math.sin(node.pulsePhase) * 0.5 + 1;
        const gradient = ctx.createRadialGradient(
          node.x, node.y, 0,
          node.x, node.y, node.radius * 3 * pulseSize
        );
        gradient.addColorStop(0, 'rgba(16, 185, 129, 0.8)');
        gradient.addColorStop(0.5, 'rgba(6, 182, 212, 0.4)');
        gradient.addColorStop(1, 'rgba(16, 185, 129, 0)');
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius * 3 * pulseSize, 0, Math.PI * 2);
        ctx.fill();
        
        // Core of node
        ctx.fillStyle = 'rgba(16, 185, 129, 0.9)';
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius * pulseSize, 0, Math.PI * 2);
        ctx.fill();
      });
      
      animationFrameId = requestAnimationFrame(draw);
    };
    
    draw(0);
    
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);
  
  return <canvas ref={canvasRef} className="neural-canvas" />;
};

// Animated Role Titles
const roles = [ 
  "AI/ML Engineer", 
  "Database Developer", 
  "Fullstack Developer"
];

const Header = () => {
  const handleDownloadResume = () => {
    // Create link to download resume
    const link = document.createElement('a');
    link.href = '/resume.pdf';
    link.download = 'Elisabeth_Ashley_Resume.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className='app__header app__flex'>
      <NeuralBackground />
      
      {/* Floating Tech Icons */}
      <div className="floating-icons">
        <motion.div 
          className="float-icon icon-1"
          animate={{ 
            y: [0, -20, 0],
            rotate: [0, 10, 0]
          }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        >
          <img src={images.python} alt="Python" />
        </motion.div>
        <motion.div 
          className="float-icon icon-2"
          animate={{ 
            y: [0, 15, 0],
            rotate: [0, -8, 0]
          }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
        >
          <img src={images.react} alt="React" />
        </motion.div>
        <motion.div 
          className="float-icon icon-3"
          animate={{ 
            y: [0, -15, 0],
            x: [0, 10, 0]
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        >
          <img src={images.flutter} alt="Flutter" />
        </motion.div>
        <motion.div 
          className="float-icon icon-4"
          animate={{ 
            y: [0, 20, 0],
            rotate: [0, -15, 0]
          }}
          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
        >
          <img src={images.apex} alt="Oracle APEX" />
        </motion.div>
      </div>

      <motion.div
        whileInView={{ x: [-100, 0], opacity: [0, 1] }}
        transition={{ duration: 0.5 }}
        className='app__header-info'
      >
        <div className='app__header-badge'>
          
          <div className='tag-cmp app__flex header-small'>
            <div className='bio-text'>
              {/* Animated Greeting */}
              <motion.div 
                className="greeting-container"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <span className="hi-text">Hi, I'm</span>
                <h1 className="name-gradient">Elisabeth</h1>
              </motion.div>

              {/* Animated Role Subtitle - Typewriter Effect */}
              <motion.div 
                className="role-container"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <div className="role-wrapper">
                  <span className="role-prefix">{'<'}</span>
                  <span className="role-title">
                    <Typewriter
                      options={{
                        strings: roles,
                        autoStart: true,
                        loop: true,
                        deleteSpeed: 30,
                        delay: 50,
                        pauseFor: 2000,
                      }}
                    />
                  </span>
                  <span className="role-suffix">{' />'}</span>
                </div>
              </motion.div>

              <motion.p 
                className='p-job'
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
              >
                Software Engineer III at <a className='p-job-link' href='https://www.elastiflow.com/' target='_blank' rel='noopener noreferrer'>ElastiFlow</a>, and founder and developer of <a className='p-job-link' href='https://civixthread.com' target='_blank' rel='noopener noreferrer'>CivixThread</a>
              </motion.p>
              
              <div className='app__header-social-inline'>
                <SocialMedia />
              </div>
          
              <motion.p 
                className='p-bio spacing'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9 }}
              >
                Since graduating with a Computer Science degree, I've held roles as an Oracle APEX Developer, Application Developer, Database Developer at{' '}
                <a className='p-job-link' href='https://impactintegratedtech.com' target='_blank' rel='noopener noreferrer'>Impact Integrated Technologies</a>
                , and now Software Engineer III at{' '}
                <a className='p-job-link' href='https://www.elastiflow.com/' target='_blank' rel='noopener noreferrer'>ElastiFlow</a>
                . This full-stack experience has given me a strong foundation in designing scalable systems and data-driven applications. I'm currently expanding my expertise in AI through Columbia University's AI Engineering Full Executive Program. I've also built{' '}
                <a className='p-job-link' href='https://civixthread.com' target='_blank' rel='noopener noreferrer'>
                  CivixThread
                </a>
                , a civic engagement app end-to-end, spanning database design, backend APIs, and frontend development.
              </motion.p>   
              {/* <motion.p 
                className='p-bio spacing quote-text'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.1 }}
              >
                "Those who have the privilege to know, have the duty to act." – Albert Einstein
              </motion.p> */}
            </div>
            <div className="app__header-right">
              <motion.div 
                className="app__header-img"
                initial={{ opacity: 0, scale: 0.8, rotate: -5 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ duration: 0.8, delay: 0.3, type: "spring" }}
              >
                <div className="profile-glow"></div>
                <img src={images.profile} alt="profile_bg" />
              </motion.div>
              
              {/* Resume Download Button */}
              <motion.div 
                className='app__header-cta'
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1 }}
              >
                <motion.button
                  className="resume-btn"
                  onClick={handleDownloadResume}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="resume-btn-bg"></span>
                  <span className="resume-btn-content">
                    <svg className="download-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 3V16M12 16L7 11M12 16L17 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M3 20H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    <span>Resume</span>
                    <span className="btn-particles">
                      <span></span>
                      <span></span>
                      <span></span>
                    </span>
                  </span>
                </motion.button>
              </motion.div>
            </div> 
          </div>

           
        </div>
        
      </motion.div>
    </div>
  )
}

export default AppWrap(Header, 'home')
